import {
  DeckCardSectionDetailsTilesView,
  type DeckCardTile,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.__views.details-tiles";
import { DeckCardSectionGlyphTilesView } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.__views.glyph-tiles";
import { DeckCardSectionListView } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.__views.list";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { DeckDisplayMode } from "@-/dev.improve.haniverse.routes.nav.app.deck.entities";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeStagesByGrapheme } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import type { ReactElement } from "react";
import { View } from "@-/com.npmjs.react-native";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export type { DeckCardTile };

export function DeckCardListSection({
  cards,
  deckCardTilesByCharacter,
  displayMode,
  emptyMessage,
  footer,
  isEditing,
  onRemoveCard,
  pendingDetailsFronts,
  stageByGrapheme,
}: {
  /**
   * The cards to render, in order. A section that renders incrementally passes only the window
   * it has revealed so far (see `useDeckSectionCards`); entries may be provisional actor-only
   * slices — list them in `pendingDetailsFronts` so the tiles show their loading affordance.
   */
  cards: MorphemeDetailsTileSlice[];
  deckCardTilesByCharacter?: Map<string, DeckCardTile[]>;
  displayMode: DeckDisplayMode;
  emptyMessage: string;
  /**
   * Rendered below the cards — a "more below" spinner while the section still has cards it has
   * not revealed, or details still loading. Takes the place of `emptyMessage` when `cards` is
   * empty: an empty window with a footer is "still loading", not "nothing here".
   */
  footer?: ReactElement | null;
  isEditing: boolean;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** Fronts whose `cards` entry is a provisional actor-only slice (details still loading). */
  pendingDetailsFronts?: ReadonlySet<string>;
  /** Stage fills derived from the deck's own actor rows, for both tile grids. */
  stageByGrapheme: MorphemeStagesByGrapheme;
}) {
  if (hasLength(cards, 0)) {
    if (footer != null) {
      return footer;
    }
    return (
      <View className="items-center px-4 py-6">
        <Text className="text-center text-sm text-muted-foreground">{emptyMessage}</Text>
      </View>
    );
  }

  if (displayMode === "morpheme-details-tiles") {
    return (
      <DeckCardSectionDetailsTilesView
        cards={cards}
        deckCardTilesByCharacter={deckCardTilesByCharacter}
        footer={footer}
        isEditing={isEditing}
        onRemoveCard={onRemoveCard}
        pendingDetailsFronts={pendingDetailsFronts}
        stageByGrapheme={stageByGrapheme}
      />
    );
  }

  if (displayMode === "morpheme-glyph-tiles") {
    return (
      <DeckCardSectionGlyphTilesView
        cards={cards}
        deckCardTilesByCharacter={deckCardTilesByCharacter}
        footer={footer}
        stageByGrapheme={stageByGrapheme}
      />
    );
  }

  return (
    <DeckCardSectionListView
      cards={cards}
      footer={footer}
      isEditing={isEditing}
      onRemoveCard={onRemoveCard}
      pendingDetailsFronts={pendingDetailsFronts}
    />
  );
}
